// ArtifactsLibraryPage.jsx — Task 11. The Artifacts library: a search + template-filter grid over
// everything the Vesta Agent has produced (durable agent.report projections). Each card links to the
// viewer and can copy its in-app share link. Company-wide in V1 (DEC-11-E).
//
// Data: GET /api/agent/companies/:slug/artifacts → { artifacts:[{id,title,template_key,period,
// created_at,session_id,agent_job_id,creator}] }. Presentation only — no agent-authored code here.
import React, { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { TEMPLATE_ORDER, badgeFor } from "../shell/artifacts/templateViewConfig"
import { artifactModeFromSearch, artifactModePath } from "../shell/artifactModePath"

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" })
  } catch (_e) {
    return ""
  }
}

function TypeBadge({ templateKey }) {
  const b = badgeFor(templateKey)
  const tint = `color-mix(in srgb, ${b.color} 12%, transparent)`
  return (
    <span className="afq-badge" style={{ color: b.color, background: tint }}>{b.label}</span>
  )
}

function ArtifactCard({ artifact, onOpen }) {
  const [copied, setCopied] = useState(false)
  const share = () => {
    const url = window.location.origin + "/artifacts/" + artifact.id
    try {
      navigator.clipboard.writeText(url)
    } catch (_e) {
      /* clipboard may be unavailable in some contexts — the toast still confirms intent */
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 1600)
  }
  const source = artifact.agent_job_id ? "Agent Job" : artifact.creator ? artifact.creator.name : "Chat with Vesta"
  return (
    <div className="afq-card2">
      <div className="afq-card2-top">
        <TypeBadge templateKey={artifact.template_key} />
        <span className="afq-card2-date">{fmtDate(artifact.created_at)}</span>
      </div>
      <button className="afq-card2-title" onClick={() => onOpen(artifact.id)}>{artifact.title}</button>
      <div className="afq-card2-src">{source}{artifact.period ? " · " + artifact.period : ""}</div>
      <div className="afq-card2-acts">
        <button className="afq-act afq-act--primary" onClick={() => onOpen(artifact.id)}>Open</button>
        <button className="afq-act" onClick={share}>{copied ? "Link copied" : "Share"}</button>
      </div>
    </div>
  )
}

export default function ArtifactsLibraryPage({ companySlug }) {
  const navigate = useNavigate()
  const currentMode = artifactModeFromSearch(window.location.search)
  const [state, setState] = useState({ loading: true, error: false, artifacts: [] })
  const [q, setQ] = useState("")
  const [filter, setFilter] = useState("all")

  useEffect(() => {
    let live = true
    if (!companySlug) {
      setState({ loading: false, error: true, artifacts: [] })
      return
    }
    fetch(`/api/agent/companies/${companySlug}/artifacts`, { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (live) setState({ loading: false, error: false, artifacts: data.artifacts || [] })
      })
      .catch(() => {
        if (live) setState({ loading: false, error: true, artifacts: [] })
      })
    return () => {
      live = false
    }
  }, [companySlug])

  // Chips: "All" + the registered template keys that actually have artifacts (in canonical order).
  const chips = useMemo(() => {
    const present = new Set(state.artifacts.map((a) => a.template_key))
    return ["all", ...TEMPLATE_ORDER.filter((k) => present.has(k))]
  }, [state.artifacts])

  const items = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return state.artifacts.filter(
      (a) =>
        (filter === "all" || a.template_key === filter) &&
        (!needle || (a.title || "").toLowerCase().includes(needle))
    )
  }, [state.artifacts, q, filter])

  const openArtifact = (id) => navigate(artifactModePath(`/artifacts/${id}`, currentMode))

  return (
    <div className="afq" data-page="artifacts-library">
      <div className="afq-wrap">
        <h1 className="afq-title">
          AI Documents
          <span className="vs-admin-badge">Admin only</span>
        </h1>
        <p className="afq-sub">Everything Vesta has produced for you — reports, recaps, and digests — in one place.</p>

        <div className="afq-tools">
          <div className="afq-search">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search AI documents"
              aria-label="Search AI documents"
            />
          </div>
          <div className="afq-chips" role="tablist" aria-label="Filter by type">
            {chips.map((c) => (
              <button
                key={c}
                role="tab"
                aria-selected={filter === c}
                className={"afq-chip" + (filter === c ? " on" : "")}
                onClick={() => setFilter(c)}
              >
                {c === "all" ? "All" : badgeFor(c).label}
              </button>
            ))}
          </div>
        </div>

        {state.loading ? (
          <div className="afq-note">Loading AI documents…</div>
        ) : state.error ? (
          <div className="afq-note">Couldn’t load AI documents. Try again.</div>
        ) : items.length === 0 ? (
          <div className="afq-empty">
            <h3>No AI documents match</h3>
            <p>Nothing Vesta has produced matches that search and filter.</p>
            <button
              onClick={() => {
                setQ("")
                setFilter("all")
              }}
            >
              Clear search &amp; filters
            </button>
          </div>
        ) : (
          <div className="afq-grid">
            {items.map((a) => (
              <ArtifactCard key={a.id} artifact={a} onOpen={openArtifact} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
